import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useTranslation from '../hooks/useTranslation';

const CookieConsentBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.theme.color.secondaryBg};
  border-top: 2px solid ${props => props.theme.color.border};
  padding: 20px;
  z-index: 10000;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 600;
  color: ${props => props.theme.color.darkerText};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Description = styled.p`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.color.lighterText};
  margin: 0;
  line-height: 1.5;

  a {
    color: ${props => props.theme.color.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
  }
`;

const AcceptButton = styled(Button)`
  background: ${props => props.theme.color.primary};
  color: white;

  &:hover {
    background: ${props => props.theme.color.primary}dd;
  }
`;

const RejectButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.color.lighterText};
  border: 1px solid ${props => props.theme.color.border};

  &:hover {
    background: ${props => props.theme.color.border};
  }
`;

const CustomizeButton = styled(Button)`
  background: ${props => props.theme.color.secondaryBg};
  color: ${props => props.theme.color.primaryText};
  border: 1px solid ${props => props.theme.color.border};

  &:hover {
    background: ${props => props.theme.color.border};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.color.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.color.background};
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 20px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.md};
  font-weight: 600;
  color: ${props => props.theme.color.darkerText};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${props => props.theme.color.lighterText};
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: ${props => props.theme.color.border};
  }
`;

const CookieCategory = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: ${props => props.theme.color.secondaryBg};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.color.border};
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CategoryTitle = styled.h4`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 600;
  color: ${props => props.theme.color.darkerText};
  margin: 0;
`;

const CategoryDescription = styled.p`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.color.lighterText};
  margin: 0;
  line-height: 1.5;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${props => props.theme.color.primary};
  }

  &:checked + span:before {
    transform: translateX(24px);
  }

  &:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.color.border};
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.color.border};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setIsVisible(true), 500);
    }
  }, []);

  const saveConsent = (consentData) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    setShowModal(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString(),
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString(),
    };
    saveConsent(onlyNecessary);
  };

  const handleCustomize = () => {
    setShowModal(true);
  };

  const handleSavePreferences = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString(),
    });
  };

  const togglePreference = (key) => {
    if (key !== 'necessary') {
      setPreferences(prev => ({
        ...prev,
        [key]: !prev[key],
      }));
    }
  };

  if (!isVisible && !showModal) return null;

  return (
    <>
      {isVisible && (
        <CookieConsentBanner>
          <Container>
            <Content>
              <Title>
                <span>🍪</span>
                {t('cookie.title', 'Utilisation des cookies')}
              </Title>
              <Description>
                {t('cookie.description', 'Nous utilisons des cookies pour améliorer votre expérience de navigation, analyser le trafic du site et personnaliser le contenu. Vous pouvez choisir d\'accepter tous les cookies, de les refuser ou de personnaliser vos préférences.')}{' '}
                <a href="/legal/rgpd" target="_blank" rel="noopener noreferrer">
                  {t('cookie.learnMore', 'En savoir plus')}
                </a>
              </Description>
            </Content>
            <ButtonGroup>
              <AcceptButton onClick={handleAcceptAll}>
                {t('cookie.acceptAll', 'Tout accepter')}
              </AcceptButton>
              <RejectButton onClick={handleRejectAll}>
                {t('cookie.rejectAll', 'Refuser')}
              </RejectButton>
              <CustomizeButton onClick={handleCustomize}>
                {t('cookie.customize', 'Personnaliser')}
              </CustomizeButton>
            </ButtonGroup>
          </Container>
        </CookieConsentBanner>
      )}

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('cookie.customizeTitle', 'Personnaliser les cookies')}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <CookieCategory>
              <CategoryHeader>
                <CategoryTitle>{t('cookie.necessary', 'Cookies nécessaires')}</CategoryTitle>
                <Toggle>
                  <ToggleInput
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                  />
                  <ToggleSlider />
                </Toggle>
              </CategoryHeader>
              <CategoryDescription>
                {t('cookie.necessaryDesc', 'Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés. Ils sont généralement définis en réponse à des actions que vous effectuez et qui constituent une demande de services (connexion, formulaires, etc.).')}
              </CategoryDescription>
            </CookieCategory>

            <CookieCategory>
              <CategoryHeader>
                <CategoryTitle>{t('cookie.analytics', 'Cookies analytiques')}</CategoryTitle>
                <Toggle>
                  <ToggleInput
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => togglePreference('analytics')}
                  />
                  <ToggleSlider />
                </Toggle>
              </CategoryHeader>
              <CategoryDescription>
                {t('cookie.analyticsDesc', 'Ces cookies nous permettent de compter les visites et les sources de trafic afin de mesurer et d\'améliorer les performances de notre site.')}
              </CategoryDescription>
            </CookieCategory>

            <CookieCategory>
              <CategoryHeader>
                <CategoryTitle>{t('cookie.marketing', 'Cookies marketing')}</CategoryTitle>
                <Toggle>
                  <ToggleInput
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => togglePreference('marketing')}
                  />
                  <ToggleSlider />
                </Toggle>
              </CategoryHeader>
              <CategoryDescription>
                {t('cookie.marketingDesc', 'Ces cookies peuvent être définis par nos partenaires publicitaires pour créer un profil de vos intérêts et vous montrer des publicités pertinentes sur d\'autres sites.')}
              </CategoryDescription>
            </CookieCategory>

            <CookieCategory>
              <CategoryHeader>
                <CategoryTitle>{t('cookie.preferences', 'Cookies de préférences')}</CategoryTitle>
                <Toggle>
                  <ToggleInput
                    type="checkbox"
                    checked={preferences.preferences}
                    onChange={() => togglePreference('preferences')}
                  />
                  <ToggleSlider />
                </Toggle>
              </CategoryHeader>
              <CategoryDescription>
                {t('cookie.preferencesDesc', 'Ces cookies permettent au site de se souvenir de vos choix (comme votre nom d\'utilisateur, votre langue ou la région où vous vous trouvez) et de fournir des fonctionnalités améliorées et plus personnelles.')}
              </CategoryDescription>
            </CookieCategory>

            <ModalFooter>
              <AcceptButton onClick={handleSavePreferences} style={{ flex: 1 }}>
                {t('cookie.savePreferences', 'Enregistrer mes préférences')}
              </AcceptButton>
              <RejectButton onClick={() => setShowModal(false)} style={{ flex: 1 }}>
                {t('cookie.cancel', 'Annuler')}
              </RejectButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default CookieConsent;

